import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { login, fetchMe } from "../../Redux/Thunks/AuthThunks";
import { ROUTES, STORAGE_KEYS } from "../../constants";
import { resolvePostLoginNavigatePath } from "../../utils/authReturnUrl";
import logoImage from "../../assets/images/logoNew.jpg";

import InlineAlert from "../../Utility/InlineAlert/InlineAlert";
import PasswordInput from "../../Utility/PasswordToggle/PassswordInput";
import UseTitle from "../../Utility/UseTitle/UseTitle";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoginSchema } from "../../FormsValidations/Validations";
import Cookies from "js-cookie";
import Label from "../../Utility/Label/Label";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import Checkbox from "../../Utility/Checkbox/Checkbox";
import ErrorMsg from "../../Utility/ErrorMsg";
import { logger } from "../../utils/logger";
import Input from "../../Utility/Input/Input";

export default function Login() {
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: {
      Email: "",
      Password: "",
    },
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useSelector((state) => state.apiStatus?.loading?.["login"]);
  const error = useSelector((state) => state.apiStatus?.error);

  const [rememberMe, setRememberMe] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    const savedEmail = localStorage.getItem(STORAGE_KEYS.REMEMBERED_EMAIL);
    const savedFlag = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === "true";

    if (savedFlag && savedEmail) {
      setValue("Email", savedEmail);
      setRememberMe(true);
    }
  }, [setValue]);

  const onSubmit = async (data) => {
    try {
      setLocalError("");
      const response = await dispatch(login({ data, buttonKey: "login" })).unwrap();

      if (response.mfaSetupRequired) {
        if (rememberMe) {
          localStorage.setItem(STORAGE_KEYS.REMEMBERED_EMAIL, data.Email);
          localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, "true");
        } else {
          localStorage.removeItem(STORAGE_KEYS.REMEMBERED_EMAIL);
          localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
        }
        Cookies.set("mfaSetupToken", response.setupToken, {
          expires: new Date(Date.now() + 15 * 60 * 1000),
          secure: true,
          sameSite: "Strict",
        });
        if (response.userId != null) {
          try {
            sessionStorage.setItem("pm_mfa_setup_user_id", String(response.userId));
          } catch {
            /* ignore */
          }
        }
        navigate(ROUTES.MFA_SETUP, { replace: true });
      } else if (response.mfaRequired) {
        if (rememberMe) {
          localStorage.setItem(STORAGE_KEYS.REMEMBERED_EMAIL, data.Email);
          localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, "true");
        } else {
          localStorage.removeItem(STORAGE_KEYS.REMEMBERED_EMAIL);
          localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
        }
        const payload = {
          userId: response.userId,
          method: response.method,
          exp: Date.now() + 5 * 60 * 1000, // 5 minutes
        };
        const encodedUserId = btoa(JSON.stringify(payload));
        logger.debug("Encoded user ID:", encodedUserId);
        Cookies.set("userId", encodedUserId, {
          expires: 1, // 1 day
          secure: true, // https only
          sameSite: "Strict",
        });
        navigate(`/mfa-login`);
      } else {
        localStorage.setItem("authToken", JSON.stringify(response));

        if (rememberMe) {
          localStorage.setItem(STORAGE_KEYS.REMEMBERED_EMAIL, data.Email);
          localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, "true");
        } else {
          localStorage.removeItem(STORAGE_KEYS.REMEMBERED_EMAIL);
          localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
        }

        // First login: must change password — do not call GET /users/me until after password is set
        if (response.requirePasswordChange) {
          navigate(ROUTES.FORCE_PASSWORD, { replace: true });
          return;
        }

        let me;
        try {
          me = await dispatch(fetchMe()).unwrap();
        } catch (e) {
          logger.warn("fetchMe after login failed", e);
        }

        navigate(resolvePostLoginNavigatePath(me, location), { replace: true });
      }
    } catch (err) {
      logger.error("Login failed:", err);
      if (typeof err === "object") {
        if (err?.errors) {
          Object.entries(err.errors).forEach(([field, message]) => {
            setError(field, { type: "manual", message });
          });
        } else if (err?.message) {
          setLocalError(err.message);
        } else {
          setLocalError("Invalid email or password. Please try again.");
        }
      } else {
        setLocalError("Invalid email or password. Please try again.");
      }
    }
  };

  useEffect(() => {
    if (error) {
      if (typeof error === "object") {
        Object.entries(error).forEach(([field, message]) => {
          setError(field, { type: "manual", message });
        });
      } else {
        setLocalError(error);
        const timer = setTimeout(() => {
          setLocalError("");
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [error, setError]);

  return (
    <div>
      <UseTitle formattedTitle="Login" />
      <div className="auth-page-wrapper ">
        <div className="auth-page-content pb-0">
          <div className="w-full max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center h-screen">
              <div className="w-full md:w-2/3 lg:w-1/2 xl:w-2/5">
                <div className="bg-surface-primary rounded-md border border-border-primary shadow-sm mt-4">
                  <div className="mt-5">
                    <div className="flex justify-center items-center ">
                      <img
                        src={logoImage}
                        alt="Logo"
                        className="h-[72px] w-auto max-w-[280px] object-contain"
                      />
                    </div>
                  </div>
                  <div className="p-4 mt-4">
                    {localError && (
                      <InlineAlert className="mb-3" severity="error">
                        {localError}
                      </InlineAlert>
                    )}
                    <div className="p-2">
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="mb-3">
                          <Label htmlFor="email">Email</Label>
                          <Input
                            type="text"
                            {...register("Email")}
                            className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                            id="email"
                            placeholder="Enter email"
                            noWrapper={true}
                          />
                          <ErrorMsg message={errors.Email?.message} className="mt-1" />
                        </div>
                        <div className="mb-3">
                          <div className="float-right mb-2">
                            <Link
                              to="/forgot-password"
                              className="text-text-secondary hover:text-theme-primary transition-colors"
                            >
                              Forgot password?
                            </Link>
                          </div>
                          <Label htmlFor="password-input">Password</Label>
                          <div className="relative mb-3">
                            <PasswordInput
                              register={register}
                              name="Password"
                              id="password-input"
                              placeholder="Enter password"
                              autoComplete="current-password"
                            />
                            <ErrorMsg message={errors.Password?.message} className="mt-1" />
                          </div>
                        </div>
                        <Checkbox
                          id="auth-remember-check"
                          name="rememberMe"
                          checked={rememberMe}
                          onChange={() => setRememberMe(!rememberMe)}
                          label="Remember me"
                        />
                        <div className="mt-4">
                          <FormSubmitButton
                            loading={loading}
                            label="Sign In"
                            idleIconClass=""
                            className="w-full flex items-center justify-center gap-2"
                          />
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-center"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
